import React, { useEffect, useMemo } from 'react';

import { observer } from 'mobx-react';
import { Container, Pagination } from '@infoods/lifebase-ui';

import classes from './NgzCoachList.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { EmptyList } from '../../../shared/components/ui-components';
import { getVisibleColumns } from '../../../shared/components/table-components';
import { CoachListPageTitle } from './CoachListPageTitle/CoachListPageTitle';
import { TitleRow } from './TitleRow/TitleRow';
import { FilterRow } from './FilterRow/FilterRow';
import { CoachRow } from './CoachRow/CoachRow';
import {
  COACH_LIST_COLUMN,
  COACH_LIST_MODE,
  COLUMN_WIDTHS,
  CoachListMode,
  ShowColumnsConfig,
} from './NgzCoachList.settings';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { useUserRoleLabel } from '../../../shared/hooks/useUserRoleLabel';
import { useCoachListStore } from './hooks/useCoachListStore';

type NgzCoachListProps = {
  listMode: CoachListMode;
};

const NgzCoachList = observer(({ listMode }: NgzCoachListProps) => {
  const { isSuperAdmin, paymentFlowType } = loggedInUserDataStore;
  const { coachList, getCoachList, filterMethods } = useCoachListStore(listMode);

  const { getForAgent } = useUserRoleLabel();

  const { isShowPlaceholder, isSearchUsed, isShowPagination, getPaginationProps } =
    filterMethods;

  useEffect(() => {
    getCoachList();
  }, []);

  const paginationProps = getPaginationProps();

  const showColumnsConfig = useMemo<ShowColumnsConfig>(
    () => ({
      [COACH_LIST_COLUMN.NAME]: true,
      [COACH_LIST_COLUMN.ENTITY]: isSuperAdmin,
      [COACH_LIST_COLUMN.STATE]: isSuperAdmin,
      [COACH_LIST_COLUMN.CITY]: isSuperAdmin,
      [COACH_LIST_COLUMN.SETUP_COMMISSION]:
        listMode === COACH_LIST_MODE.NUTRITION &&
        (isSuperAdmin || !paymentFlowType.NEW_USER_FLOW_2024),
      [COACH_LIST_COLUMN.RENEW_COMMISSION]:
        listMode === COACH_LIST_MODE.NUTRITION &&
        (isSuperAdmin || !paymentFlowType.NEW_USER_FLOW_2024),
      [COACH_LIST_COLUMN.COACH_DESIGNATION]: true,
      [COACH_LIST_COLUMN.STATUS]: true,
      [COACH_LIST_COLUMN.EXTRA_INFO]: true,
    }),
    [listMode, isSuperAdmin, paymentFlowType.NEW_USER_FLOW_2024],
  );

  const { visibleColumns, gridTemplateColumns } = getVisibleColumns({
    columnEnum: COACH_LIST_COLUMN,
    showColumnsConfig,
    columnWidths: COLUMN_WIDTHS,
  });

  return (
    <Container>
      <CoachListPageTitle listMode={listMode} />
      <div
        className={classes.coachTable}
        style={{ display: 'grid', gridTemplateColumns }}
      >
        <TitleRow visibleColumns={visibleColumns} />
        <FilterRow visibleColumns={visibleColumns} listMode={listMode} />
        {coachList.map((coachInfo) => (
          <CoachRow
            key={coachInfo.id}
            coachInfo={coachInfo}
            visibleColumns={visibleColumns}
            listMode={listMode}
          />
        ))}
      </div>
      <EmptyList
        show={isShowPlaceholder}
        isSearch={isSearchUsed}
        description={`There are no ${getForAgent({ pluralize: true, case: 'lower' })}`}
      />
      {isShowPagination && <Pagination {...paginationProps} />}
    </Container>
  );
});

angularizeWithProviders(NgzCoachList, 'ngzCoachList', {
  listMode: '@',
});

export { NgzCoachList };
